import { BaseLogger } from "base_logger";

const RETRY_BACKOFF = 1000;

const wait = (delay: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay);
  });

abstract class BaseAPI extends BaseLogger {
  public userId: string;

  constructor(
    public baseUrl: string,
    userId?: string,
  ) {
    super();
    this.userId = userId || "";
  }

  abstract log(...args: unknown[]): void;

  abstract warn(...args: unknown[]): void;

  abstract error(...args: unknown[]): void;

  abstract getHeaders(
    method: "POST" | "GET",
    url: string,
    sendAsFormData: boolean,
  ): {
    Accept: string;
    "X-CSRFToken"?: string;
    "Content-Type"?: string;
  };

  getUrl(path: string) {
    return `${this.baseUrl}/${path}`;
  }

  abstract requestErrorCallback(
    url: string,
    method: "POST" | "GET",
    data: string | FormData,
    status: number | string,
  ): void;

  protected async sendRequest(
    method: "POST" | "GET",
    urlPath: string,
    data: string | FormData = "",
    sendAsFormData = false,
    retries = 0,
    backoff = RETRY_BACKOFF,
  ): Promise<Response> {
    const startTime = Date.now();
    const url = this.getUrl(urlPath);
    this.log(`Started request: ${method} ${url} for User ${this.userId}`);

    const headers = this.getHeaders(method, url, sendAsFormData);

    try {
      const response = await fetch(url, {
        method,
        mode: "cors",
        cache: "no-cache",
        credentials: "include", // 'same-origin',
        headers,
        redirect: "follow",
        referrerPolicy: "origin",
        body: method === "POST" ? data : undefined,
      });
      if (response.ok) {
        const duration = Date.now() - startTime;
        this.log(
          `Completed request (status: ${response.status}): ${method} ${url} for User ${this.userId} in ${duration} ms`,
        );
      } else {
        this.error(
          `Error during request (status: ${response.status}): ${method} ${url} for User ${
            this.userId || "undefined"
          }`,
        );
        this.requestErrorCallback(url, method, data, response.status);
      }
      return response;
    } catch (error) {
      // If we end up here, that usually means network issues.
      if (retries > 0) {
        return wait(RETRY_BACKOFF).then(() => {
          this.warn(
            `Retrying failed request (retries: ${retries}): ${method} ${url} for User ${
              this.userId || "undefined"
            }`,
          );
          return this.sendRequest(method, urlPath, data, sendAsFormData, retries - 1, backoff * 2);
        });
      }
      this.error(
        `Error during request (promise rejected): ${method} ${url} for User ${
          this.userId || "undefined"
        } - ${error}`,
      );
      this.requestErrorCallback(url, method, data, "network-error");
      throw error;
    }
  }

  post(urlPath: string, data: string | FormData = "", sendAsFormData = false, retries = 0) {
    return this.sendRequest("POST", urlPath, data, sendAsFormData, retries);
  }

  get(urlPath: string, retries = 0) {
    return this.sendRequest("GET", urlPath, "", false, retries);
  }
}

export { BaseAPI, wait };
